import {useTranslation} from 'react-i18next';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {IValidationErrorObject} from '../../../../incase-domain/common/validationErrorResponse';
import {FLIP_CARD_SIDE, IElementFlipCard} from '../../../../incase-domain/elements/flipCard';
import ElementShadow from '../common/elementShadow';
import FlipCardElementSelect from './FlipCardElementSelect';
import {TABS} from '../../form/part/tabs';
import AnimationForm from '../common/animationForm';
import ChecklistTile from '../../../../incase-domain/components/checkbox/ChecklistTile';
import SectionTitle from '../../../elements/common/SectionTitle';
import {flipCardAction} from '../../../../store/actions/flipCard.actions';
import {getFlipCardSide} from '../../../../store/selectors/flipCard.selector';
import {AppState} from '../../../../store/reducer';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedFlipCardElementComponentProps {
    errors: IValidationErrorObject | null;
    activeTab: string;
}

export default function HighlightedFlipCardElementComponent(props: HighlightedFlipCardElementComponentProps): JSX.Element {
    const {errors, activeTab} = props;
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const highlightedElement = useHighlightedElement() as IElementFlipCard;
    const updateHighlightedElement = useSetHighlightedElement();

    const facingSide = useSelector((state: AppState) => getFlipCardSide(state, highlightedElement.id));

    useEffect(() => {
        if (activeTab === TABS.FLIP_CARD_FRONT.id) {
            dispatch(flipCardAction(highlightedElement.id, FLIP_CARD_SIDE.FRONT));
        } else if (activeTab === TABS.FLIP_CARD_BACK.id) {
            dispatch(flipCardAction(highlightedElement.id, FLIP_CARD_SIDE.BACK));
        }
    }, [activeTab, dispatch, facingSide, highlightedElement.id]);

    if (activeTab === TABS.STYLE.id) {
        return <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />;
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <div className="l-group--form">
                    <SectionTitle label={t('designSidepanel.preferences')} />
                    <ChecklistTile
                        value={highlightedElement.flipCardOnHover}
                        label={t('flipCard.flipCardOnHover')}
                        help={t('flipCard.flipCardOnHoverTooltip')}
                        onChange={(value) => updateHighlightedElement.single('flipCardOnHover', value)}
                    />
                </div>
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    if (activeTab === TABS.FLIP_CARD_FRONT.id) {
        return (
            <FlipCardElementSelect
                errors={errors}
                flipCardSide={FLIP_CARD_SIDE.FRONT}
                highlightedElement={highlightedElement}
                highlightedElementChangeHandler={updateHighlightedElement}
            />
        );
    }

    return (
        <>
            <FlipCardElementSelect
                errors={errors}
                flipCardSide={FLIP_CARD_SIDE.BACK}
                highlightedElement={highlightedElement}
                highlightedElementChangeHandler={updateHighlightedElement}
            />
        </>
    );
}
